import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import { bookInfo } from '../mockData';
import SEO from '../components/SEO';

const Book = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Safar - An Immigrant's Journey of Life & Leadership"
        description="Bestselling book by Ravi Prakash. A deeply inspiring story of resilience, reinvention, and leadership. Perfect for professionals in transition, immigrants, graduates, and leaders seeking perspective. Available on Amazon."
        canonical="/book"
        ogImage="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/6qxmtcum__DSC3776.jpg"
      />

      {/* Hero Section */}
      <section className="section-padding bg-safar-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Hero Content - Text First on Mobile */}
            <div className="order-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
                A book about resilience, reinvention, and leadership built the hard way
              </h1>
              <p className="text-lg md:text-xl font-sans text-white mb-8 leading-relaxed">
                <strong className="text-white">Safar: An Immigrant's Journey of Life & Leadership</strong> is for readers who know that growth is rarely clean, certainty is overrated, and courage often comes before confidence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a
                  href={bookInfo.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-safar-blue hover:bg-gray-100 font-sans font-semibold px-8 py-4 rounded-lg transition-colors text-center"
                >
                  Buy on Amazon
                </a>
                <a 
                  href="https://calendly.com/journeywithravi/30" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-safar-orange text-white hover:bg-burnt-orange font-sans font-semibold px-8 py-4 rounded-lg transition-colors text-center"
                >
                  Book a Signing Event
                </a>
              </div>
              
              <p className="text-sm text-white font-sans">
                Also available for author speaking events, campus events, and corporate book conversations.
              </p>
            </div>
            
            {/* Book Image */}
            <div className="flex justify-center order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/aqlqzcjk_BookCoverMockup.png"
                alt="Safar Book Cover"
                className="w-full max-w-md lg:max-w-lg rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What the Book Is About */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            What readers will find in Safar
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              Safar is part personal journey, part leadership reflection, and part reminder that the hardest roads often teach the deepest lessons.
            </p>
            
            <div className="bg-white rounded-lg p-8 mt-8">
              <p className="text-lg font-sans font-semibold text-charcoal mb-4">
                Through lived experience, Ravi explores:
              </p>
              <ul className="space-y-3">
                {[
                  'Resilience through uncertainty',
                  'Reinvention after setbacks',
                  'Identity, belonging, and growth',
                  'Leadership rooted in humanity',
                  'The inner work required to keep moving forward'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <BookOpen className="h-6 w-6 text-safar-orange mr-3 flex-shrink-0 mt-1" />
                    <span className="text-base font-sans text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-lg font-sans text-gray-800 mt-8 pt-6 border-t border-gray-200">
                This is not a book of polished theory.<br />
                <strong>It is a book shaped by real life.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Who this book is for
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {bookInfo.idealFor.map((audience) => (
              <div
                key={audience}
                className="bg-soft-stone rounded-lg p-6 border-l-4 border-safar-orange"
              >
                <p className="text-lg font-sans text-gray-800">{audience}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-lg font-sans text-gray-700 mt-12 max-w-2xl mx-auto italic">
            This book will resonate with anyone who has ever had to rebuild, adapt, push forward, and grow through uncertainty.
          </p>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Why this message matters now
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              We are living in a time of pressure, speed, identity shifts, and constant change.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              People are being asked to adapt faster, perform more, and stay steady in environments that often make reflection difficult and reinvention necessary.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              <strong>Safar speaks to that reality with honesty, humility, and perspective.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Reviews/Endorsements Placeholder */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            What readers are saying
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-soft-white border border-gray-200 rounded-lg p-8">
              <p className="text-lg font-sans text-gray-800 italic mb-6 leading-relaxed">
                "A deeply human and thoughtful reflection on resilience, leadership, and becoming."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-sans font-semibold text-charcoal">
                  Early Reviewer
                </p>
              </div>
            </div>
            
            <div className="bg-soft-white border border-gray-200 rounded-lg p-8">
              <p className="text-lg font-sans text-gray-800 italic mb-6 leading-relaxed">
                "This book speaks to anyone who has ever had to begin again."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-sans font-semibold text-charcoal">
                  Amazon Reviewer
                </p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <a
              href={bookInfo.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center text-lg px-8 py-4"
            >
              Buy the Book
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Bulk/Event Section */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-charcoal mb-6">
            Bring Safar to your event
          </h2>
          
          <p className="text-lg font-sans text-gray-700 mb-8 max-w-2xl mx-auto">
            Ravi is available for:
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
            {[
              'Author talks',
              'Book signings',
              'Conference appearances',
              'University speaking events',
              'Book-centered leadership conversations',
              'Bulk book orders'
            ].map((item) => (
              <div
                key={item}
                className="bg-white rounded-lg p-4 border border-gray-200"
              >
                <p className="text-sm font-sans text-gray-800">{item}</p>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Book Ravi
            </a>
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Ask About Book Events
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-near-black text-white section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-white mb-10">
            Buy the book. Bring the author. Extend the conversation.
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={bookInfo.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-safar-orange text-white hover:bg-burnt-orange font-sans font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Buy on Amazon
            </a>
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-near-black font-sans font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book a Signing Event
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Book;
